import axios from "axios";

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:3001/api";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // For Auth0, the token will be passed directly to individual API calls
    // This allows components to get fresh tokens from Auth0 context
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for progress API - it should fail gracefully
      const isProgressEndpoint = error.config?.url?.includes("/progress");

      if (!isProgressEndpoint) {
        // Signal that auth has expired
        localStorage.setItem("auth_expired", "true");
        // Only redirect if we're not already on the login page
        if (window.location.pathname !== "/login") {
          window.location.href = "/login";
        }
      }
    }
    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  username: string;
  email: string;
  role: string;
}

export interface Collection {
  id: number;
  name: string;
  description: string;
  created_at: string;
  cloze_count: number;
  creator_name?: string;
}

export interface Cloze {
  id: number;
  collection_id: number;
  greek_sentence: string;
  english_word: string;
  correct_word: string;
  position: number;
  context_text?: string;
  reference?: string;
  user_correct?: boolean | null;
  attempts?: number;
  choices?: Choice[];
}

export interface Choice {
  id: number;
  cloze_id: number;
  word: string;
  is_correct: boolean;
}

export interface BibleVerse {
  id: number;
  book: string;
  chapter: number;
  verse_number: number;
  reference: string;
  full_text: string;
  words_data?: any;
}

export interface CustomCollection {
  id: number;
  name: string;
  description: string;
  user_id: number;
  is_public: boolean;
  exercise_type?: "clozes" | "verbs" | "both";
  created_at: string;
  updated_at: string;
  verse_count?: number;
  creator_name?: string;

  // Cloze progress tracking
  total_clozes?: number;
  attempted_clozes?: number;
  completed_clozes?: number;
  mastered_clozes?: number;
  progress?: number;
  cloze_mastery?: number;

  // Verb parsing progress tracking
  total_verbs?: number;
  attempted_verbs?: number;
  completed_verbs?: number;
  mastered_verbs?: number;
  verb_progress?: number;
  verb_mastery?: number;
}

// Auth API
export const authAPI = {
  register: (username: string, email: string, password: string) =>
    api.post("/auth/register", { username, email, password }),

  login: (username: string, password: string) =>
    api.post("/auth/login", { username, password }),

  getProfile: () => api.get("/auth/profile"),

  updateProfile: (data: { username: string; email: string }) =>
    api.put("/auth/profile", data),
};

// Cloze API
export const clozeAPI = {
  getCollections: () => api.get("/clozes/collections"),

  getClozes: (
    collectionId: number,
    getAccessTokenSilently: () => Promise<string>,
    order?: "biblical" | "random" | "devotional"
  ) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: `/clozes/collections/${collectionId}/clozes${
          order ? `?order=${order}` : ""
        }`,
      },
      getAccessTokenSilently
    ),

  submitAnswer: (
    clozeId: number,
    answer: string,
    timeTaken: number | undefined,
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "POST",
        url: `/clozes/clozes/${clozeId}/answer`,
        data: { answer, timeTaken },
      },
      getAccessTokenSilently
    ),

  getProgress: (getAccessTokenSilently: () => Promise<string>) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: "/clozes/progress",
      },
      getAccessTokenSilently
    ),
};

// Verb Parsing API
export const verbAPI = {
  getCollections: () => api.get("/verbs/collections"),

  getVerbs: (
    collectionId: number,
    getAccessTokenSilently: () => Promise<string>,
    order?: "biblical" | "random" | "devotional"
  ) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: `/verbs/collections/${collectionId}/verbs${
          order ? `?order=${order}` : ""
        }`,
      },
      getAccessTokenSilently
    ),

  submitAnswer: (
    verbId: number,
    answer: {
      wordType: "verb" | "participle" | "infinitive";
      lexicalForm: string;
      tense: string;
      voice: string;
      mood: string;
      person?: string;
      number?: string;
      gender?: string;
      case?: string;
      translation: string;
      timeTaken?: number;
    },
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "POST",
        url: `/verbs/verbs/${verbId}/answer`,
        data: answer,
      },
      getAccessTokenSilently
    ),

  initializeOptions: () => api.post("/verbs/initialize-options"),

  updateLexicalForms: () => api.post("/verbs/update-lexical-forms"),

  getProgress: (getAccessTokenSilently: () => Promise<string>) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: "/verbs/progress",
      },
      getAccessTokenSilently
    ),
};

// Admin API
export const adminAPI = {
  createCollection: (name: string, description: string) =>
    api.post("/admin/collections", { name, description }),

  getCollections: () => api.get("/admin/collections"),

  createCloze: (data: {
    collectionId: number;
    greekSentence: string;
    englishWord: string;
    correctWord: string;
    position: number;
    choices: string[];
  }) => api.post("/admin/clozes", data),

  getClozes: (collectionId: number) =>
    api.get(`/admin/collections/${collectionId}/clozes`),

  updateCloze: (
    clozeId: number,
    data: {
      greekSentence: string;
      englishWord: string;
      correctWord: string;
      position: number;
      choices: string[];
    }
  ) => api.put(`/admin/clozes/${clozeId}`, data),

  deleteCloze: (clozeId: number) => api.delete(`/admin/clozes/${clozeId}`),

  getStats: () => api.get("/admin/stats"),
};

// Custom Collections API
export const customCollectionsAPI = {
  // Get all bible verses
  getBibleVerses: (book?: string, chapter?: number) => {
    const params = new URLSearchParams();
    if (book) params.append("book", book);
    if (chapter !== undefined) params.append("chapter", chapter.toString());
    return api.get(`/custom-collections/bible-verses?${params.toString()}`);
  },

  // Get chapters for a book
  getChapters: (book: string) =>
    api.get(`/custom-collections/bible-books/${book}/chapters`),

  // Get all custom collections
  getCollections: (getAccessTokenSilently: () => Promise<string>) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: "/custom-collections",
      },
      getAccessTokenSilently
    ),

  // Create a new custom collection
  createCollection: (
    data: {
      name: string;
      description: string;
      isPublic?: boolean;
      exerciseType?: "clozes" | "verbs" | "both";
    },
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "POST",
        url: "/custom-collections",
        data,
      },
      getAccessTokenSilently
    ),

  // Get a specific custom collection
  getCollection: (
    collectionId: number,
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: `/custom-collections/${collectionId}`,
      },
      getAccessTokenSilently
    ),

  // Update a custom collection
  updateCollection: (
    collectionId: number,
    data: {
      name: string;
      description: string;
      isPublic?: boolean;
      exerciseType?: "clozes" | "verbs" | "both";
    },
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "PUT",
        url: `/custom-collections/${collectionId}`,
        data,
      },
      getAccessTokenSilently
    ),

  // Delete a custom collection
  deleteCollection: (
    collectionId: number,
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "DELETE",
        url: `/custom-collections/${collectionId}`,
      },
      getAccessTokenSilently
    ),

  // Add verses to a collection
  addVerses: (
    collectionId: number,
    verseIds: number[],
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "POST",
        url: `/custom-collections/${collectionId}/verses`,
        data: { verseIds },
      },
      getAccessTokenSilently
    ),

  // Remove a verse from a collection
  removeVerse: (
    collectionId: number,
    verseId: number,
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "DELETE",
        url: `/custom-collections/${collectionId}/verses/${verseId}`,
      },
      getAccessTokenSilently
    ),

  // Get practice info (collection_id) for a custom collection
  getPracticeInfo: (
    collectionId: number,
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: `/custom-collections/${collectionId}/practice-info`,
      },
      getAccessTokenSilently
    ),
};

// Settings API
export const settingsAPI = {
  // Get all user settings
  getSettings: (getAccessTokenSilently: () => Promise<string>) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: "/settings",
      },
      getAccessTokenSilently
    ).then((res) => res.data),

  // Update user settings
  updateSettings: (
    settings: { [key: string]: any },
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "PUT",
        url: "/settings",
        data: { settings },
      },
      getAccessTokenSilently
    ).then((res) => res.data),

  // Get a specific setting
  getSetting: (key: string, getAccessTokenSilently: () => Promise<string>) =>
    makeAuthenticatedRequest(
      {
        method: "GET",
        url: `/settings/${key}`,
      },
      getAccessTokenSilently
    ).then((res) => res.data),

  // Update a specific setting
  updateSetting: (
    key: string,
    value: any,
    getAccessTokenSilently: () => Promise<string>
  ) =>
    makeAuthenticatedRequest(
      {
        method: "PUT",
        url: `/settings/${key}`,
        data: { value },
      },
      getAccessTokenSilently
    ).then((res) => res.data),

  // Delete a specific setting
  deleteSetting: (key: string, getAccessTokenSilently: () => Promise<string>) =>
    makeAuthenticatedRequest(
      {
        method: "DELETE",
        url: `/settings/${key}`,
      },
      getAccessTokenSilently
    ).then((res) => res.data),
};

// Helper function to make authenticated API calls with Auth0 tokens
export const makeAuthenticatedRequest = async (
  config: any,
  getAccessTokenSilently: () => Promise<string>
) => {
  try {
    const token = await getAccessTokenSilently();
    const authConfig = {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${token}`,
      },
    };
    return await api(authConfig);
  } catch (error: any) {
    // Handle Auth0 token acquisition errors
    if (
      error.error === "login_required" ||
      error.error === "consent_required" ||
      error.error === "invalid_grant" ||
      error.message?.includes("Login required")
    ) {
      // Use localStorage to signal that a logout is needed
      localStorage.setItem("auth_expired", "true");
      window.location.href = "/login";
      throw error;
    }

    // Handle API 401 responses (expired token on server side)
    if (error.response?.status === 401) {
      console.error("API returned 401, token expired on server side");
      localStorage.setItem("auth_expired", "true");
      window.location.href = "/login";
      throw error;
    }

    throw error;
  }
};

export default api;
